import React from 'react';
import {
  CommentConversion,
  Content,
  ContentNode,
  ContentNodeType,
  ContentType,
  Epic,
  Task,
} from '@amzn/awscat-pattern-library-service-model';
import {
  convertCodeRepository,
  convertContentMetadata,
  convertContentTypeMetadata,
  convertTemplateMetadata,
  getContentSections,
  getH2Heading,
  getH3Heading,
} from '../../utils/ConverterUtils';
import JSONSerializer from '../../../common/JSONSerializer';
import ContentConverterInput from './ContentConverterInput';
import { ContentConversionSubTypeMap } from '../../utils/SelectableOptions';
import { localLog } from '../../../loggers/Logger';
import { getSkillLabels } from '../../utils/TaskUtils';
import { autoGenerateHref, concatContentData } from '../../../api/ContentUtil';
import { getJSON } from '@amzn/awscat-editor';

interface Props {
  content: Content;
  onDismiss?: () => void;
  onError: (errorMsg?: string) => void;
  onSuccess: (updatedContent: Content, commentConversions: CommentConversion[]) => void;
  toContentType: ContentType;
}

const SingleToMultiPageConverter: React.FunctionComponent<Props> = (props: Props) => {
  const {
    content,
    content: {
      contentMetadata: { contentType },
    },
    onDismiss,
    onError,
    onSuccess,
    toContentType,
  } = props;
  const contentSubType = ContentConversionSubTypeMap.get(toContentType);
  const contentSections = getContentSections(content);
  const commentConversions: CommentConversion[] = [];

  const convertToPage = (section: ContentNode) => {
    section.nodeType = ContentNodeType.Page;
  };

  const convertTask = (task: Task, section: ContentNode) => {
    const concatData = concatContentData(
      section.contentData,
      getH3Heading(task.title),
      task.description,
      getSkillLabels(task.skills),
    );
    section.contentData = getJSON(concatData);
  };

  const convertEpic = (epic: Epic, section: ContentNode) => {
    const concatData = concatContentData(
      section.contentData,
      getH2Heading(epic.title),
      epic.description,
      '<br>',
    );
    section.contentData = getJSON(concatData);
    commentConversions.push(CommentConversion.create('epics:' + epic.id, section.id));
    epic.tasks.forEach((task) => convertTask(task, section));
  };

  const convertEpicsToPage = (section: ContentNode) => {
    section.nodeType = ContentNodeType.Page;
    const epics = section.contentData;
    section.contentData = '';
    // @ts-expect-error TS7006 - strict migration
    epics.forEach((epic) => convertEpic(epic, section));
  };

  const convertTroubleshootingToPage = (section: ContentNode) => {
    let convertedContentData = '';
    if (section.contentData && Array.isArray(section.contentData)) {
      section.contentData.forEach(({ issue, solution }) => {
        convertedContentData = concatContentData(
          convertedContentData,
          issue,
          '<br>',
          solution,
          '<hr>',
        );
      });
    }
    section.contentData = getJSON(convertedContentData);
  };

  const convertSection = (section: any) => {
    switch (section.id) {
      case 'epics': {
        convertEpicsToPage(section);
        break;
      }
      case 'troubleshooting': {
        convertTroubleshootingToPage(section);
        break;
      }
      default: {
        convertToPage(section);
        break;
      }
    }
  };

  const convertNestedContentData = (contentData: ContentNode) => {
    let { contentSections: sections } = contentData;
    const summarySection = sections.find((section) => section.id === 'summary');
    contentData.contentSections = summarySection ? [summarySection] : [];
    sections = sections.filter((section) => section.id !== 'summary');
    sections.forEach((section) => convertSection(section));
    contentData.subContent = sections;
  };

  const convertContent = (updatedContent: Content) => {
    const { contentData, contentTypeMetadata, contentMetadata } = updatedContent;
    // @ts-expect-error TS2345 - strict migration
    convertContentTypeMetadata(contentTypeMetadata, contentSubType);
    convertContentMetadata(contentMetadata, toContentType);
    // @ts-expect-error TS2345 - strict migration
    convertTemplateMetadata(updatedContent, contentSubType);
    convertNestedContentData(contentData);
    convertCodeRepository(updatedContent, toContentType);
    autoGenerateHref(contentData, contentMetadata, true);
  };

  const convertSingleToMultiPage = () => {
    const serializer: JSONSerializer = new JSONSerializer();
    const updatedContent = serializer.deserialize<Content>(serializer.stringify(content), Content);
    convertContent(updatedContent);
    onSuccess(updatedContent, commentConversions);
  };

  const onConfirm = () => {
    try {
      convertSingleToMultiPage();
    } catch (err) {
      localLog.error(err);
      // @ts-expect-error TS18046 - strict migration
      onError(err.message);
    }
  };

  return (
    <ContentConverterInput
      fromSections={contentSections}
      fromContentType={contentType}
      toContentType={toContentType}
      onConfirm={onConfirm}
      onCancel={onDismiss}
    />
  );
};

export default SingleToMultiPageConverter;
